import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { SailLike } from '../net/ColyseusClient';
import { applyPbrDiscipline } from '../materials/gltfMaterials';
import { createShipMaterials } from '../materials/shipMaterials';
import { disposeObject3D } from '../utils/dispose';
import { instanceStatic, type InstanceTransform } from './Instancing';
import { ModelLibrary } from './ModelLibrary';
import { SailCloth, type ClothQuality } from './SailCloth';

const HULL_COLOR = 0x3a2b1c; // tarred, weathered timber
const DECK_COLOR = 0xb08a5e;
const TRIM_COLOR = 0x2a1c10;
const SAIL_COLOR = 0xf2ecdc; // weathered off-white
const MAST_COLOR = 0x4a2f18;
const HELM_COLOR = 0xe0a63c;
const SAIL_STATION_COLOR = 0x48baa7;
const RAILING_COLOR = 0x3a2816;
const WINDOW_COLOR = 0x88ccee;
const ROPE_COLOR = 0x7a654a;
const PLANK_DARK = 0x8a6a44;
const LANTERN_HOUSING_COLOR = 0x181008;
const LANTERN_GLOW_COLOR = 0xffb45e;
const FLAG_COLOR = 0x14100c;

/** Subtle deck station markers: small, faint, unlit inlays. */
const MARKER_INNER_RADIUS = 0.24;
const MARKER_OUTER_RADIUS = 0.34;
const MARKER_SEGMENTS = 16;
const MARKER_OPACITY = 0.32;
/** Hide station markers beyond this camera distance (metres). */
const MARKER_HIDE_DISTANCE = 30;

/** Optional per-instance recolour so a second vessel can read as hostile. */
export interface ShipPalette {
  hull?: number;
  deck?: number;
  trim?: number;
  sail?: number;
  mast?: number;
  helm?: number;
  sailStation?: number;
}

/** Level of detail for optional Black-Sails dressing. */
export type ShipDetail = 'low' | 'high';

/** Sail rendering mode: static morph ('off') or simulated cloth ('low'/'high'). */
export type ClothOption = 'off' | ClothQuality;

const DEFAULT_PALETTE: Required<ShipPalette> = {
  hull: HULL_COLOR,
  deck: DECK_COLOR,
  trim: TRIM_COLOR,
  sail: SAIL_COLOR,
  mast: MAST_COLOR,
  helm: HELM_COLOR,
  sailStation: SAIL_STATION_COLOR,
};

/**
 * Station anchors in ship-local space, matching the server's station order
 * (`helm`, `sail0`, `sail1`). The bow is local +X.
 */
export const STATION_ANCHORS: ReadonlyArray<THREE.Vector3> = [
  new THREE.Vector3(-6.0, 1.9, 0),
  new THREE.Vector3(-1.5, 1.9, 2.0),
  new THREE.Vector3(5.5, 1.9, 2.0),
];

const MAST_X = [-1.5, 5.5];

/**
 * Optional CC0 deck dressing (Kenney Pirate Kit). Each prop sits on y = 0 in
 * its own space, so it is placed with its base on the deck line; scales bring
 * the ~1.2 m Kenney props down to deck-hand size. Kept off the mobile ('low')
 * profile so the reduced draw-call budget is not spent on decorative props.
 */
const DECK_PROP_Y = 1.88;
const BARREL_SCALE = 0.6;
const CRATE_SCALE = 0.7;

const BARREL_TRANSFORMS: InstanceTransform[] = [
  { position: new THREE.Vector3(1.0, DECK_PROP_Y, -1.5) },
  { position: new THREE.Vector3(1.6, DECK_PROP_Y, 1.6) },
  { position: new THREE.Vector3(3.0, DECK_PROP_Y, -1.2) },
  { position: new THREE.Vector3(3.6, DECK_PROP_Y, 1.3) },
];

const CRATE_TRANSFORMS: InstanceTransform[] = [
  { position: new THREE.Vector3(-1.2, DECK_PROP_Y, -1.1) },
  { position: new THREE.Vector3(-1.2, DECK_PROP_Y, 1.2) },
];

const IDENTITY_EULER = new THREE.Euler();
const ONE_SCALE = new THREE.Vector3(1, 1, 1);

/** One queued mesh baked into a per-material merged geometry. */
interface StaticPart {
  geometry: THREE.BufferGeometry;
  matrix: THREE.Matrix4;
}

/** Per-material bucket of static parts that become one mesh. */
interface StaticPartSet {
  castShadow: boolean;
  parts: StaticPart[];
}

/**
 * One lofting station along the hull, from stern (-X) to bow (+X).
 *
 * The deck edge (`halfBeam`) stays at the main-deck height so `STATION_ANCHORS`
 * and the shaped deck slab share one planform, while the sheer (`gunwaleY`)
 * rises to a taffrail aft and a forecastle bulwark forward. The maximum beam
 * sits at the waterline (y = 0): the sides tumble home above it and run in to
 * the keel below it.
 */
interface HullStation {
  x: number;
  halfBeam: number;
  waterlineHalfBeam: number;
  gunwaleY: number;
  deckY: number;
  keelY: number;
  keelHalfBeam: number;
}

const HULL_STATIONS: HullStation[] = [
  { x: -8.6, halfBeam: 2.35, waterlineHalfBeam: 2.55, gunwaleY: 2.78, deckY: 1.875, keelY: -0.62, keelHalfBeam: 0.3 },
  { x: -6.0, halfBeam: 2.55, waterlineHalfBeam: 2.76, gunwaleY: 2.66, deckY: 1.875, keelY: -0.7, keelHalfBeam: 0.36 },
  { x: -3.5, halfBeam: 2.78, waterlineHalfBeam: 2.96, gunwaleY: 2.52, deckY: 1.875, keelY: -0.74, keelHalfBeam: 0.4 },
  { x: -1.5, halfBeam: 2.84, waterlineHalfBeam: 3.0, gunwaleY: 2.46, deckY: 1.875, keelY: -0.75, keelHalfBeam: 0.42 },
  { x: 0.0, halfBeam: 2.84, waterlineHalfBeam: 3.0, gunwaleY: 2.44, deckY: 1.875, keelY: -0.75, keelHalfBeam: 0.42 },
  { x: 2.0, halfBeam: 2.82, waterlineHalfBeam: 2.98, gunwaleY: 2.46, deckY: 1.875, keelY: -0.73, keelHalfBeam: 0.4 },
  { x: 4.0, halfBeam: 2.7, waterlineHalfBeam: 2.88, gunwaleY: 2.5, deckY: 1.875, keelY: -0.68, keelHalfBeam: 0.36 },
  { x: 5.5, halfBeam: 2.55, waterlineHalfBeam: 2.74, gunwaleY: 2.54, deckY: 1.875, keelY: -0.62, keelHalfBeam: 0.32 },
  { x: 7.0, halfBeam: 2.3, waterlineHalfBeam: 2.5, gunwaleY: 2.58, deckY: 1.875, keelY: -0.52, keelHalfBeam: 0.26 },
  { x: 8.5, halfBeam: 1.9, waterlineHalfBeam: 2.1, gunwaleY: 2.62, deckY: 1.875, keelY: -0.36, keelHalfBeam: 0.18 },
  { x: 9.6, halfBeam: 1.35, waterlineHalfBeam: 1.55, gunwaleY: 2.6, deckY: 1.875, keelY: -0.14, keelHalfBeam: 0.1 },
  { x: 10.4, halfBeam: 0.85, waterlineHalfBeam: 1.0, gunwaleY: 2.45, deckY: 1.875, keelY: 0.1, keelHalfBeam: 0.05 },
  { x: 11.0, halfBeam: 0.5, waterlineHalfBeam: 0.58, gunwaleY: 2.15, deckY: 1.6, keelY: 0.3, keelHalfBeam: 0.0 },
  { x: 11.6, halfBeam: 0.16, waterlineHalfBeam: 0.18, gunwaleY: 1.9, deckY: 1.4, keelY: 0.5, keelHalfBeam: 0.0 },
];

/** Linearly interpolate the hull profile at an arbitrary length position. */
function hullProfileAt(x: number): HullStation {
  const first = HULL_STATIONS[0];
  const last = HULL_STATIONS[HULL_STATIONS.length - 1];
  if (x <= first.x) return { ...first };
  if (x >= last.x) return { ...last };
  for (let i = 0; i < HULL_STATIONS.length - 1; i += 1) {
    const a = HULL_STATIONS[i];
    const b = HULL_STATIONS[i + 1];
    if (x >= a.x && x <= b.x) {
      const t = (x - a.x) / (b.x - a.x);
      return {
        x,
        halfBeam: a.halfBeam + (b.halfBeam - a.halfBeam) * t,
        waterlineHalfBeam: a.waterlineHalfBeam + (b.waterlineHalfBeam - a.waterlineHalfBeam) * t,
        gunwaleY: a.gunwaleY + (b.gunwaleY - a.gunwaleY) * t,
        deckY: a.deckY + (b.deckY - a.deckY) * t,
        keelY: a.keelY + (b.keelY - a.keelY) * t,
        keelHalfBeam: a.keelHalfBeam + (b.keelHalfBeam - a.keelHalfBeam) * t,
      };
    }
  }
  return { ...last };
}

/** Ship-side beam magnitude at a height, following the tumblehome. */
function hullSideZ(profile: HullStation, y: number): number {
  const deckY = profile.deckY;
  if (y >= deckY) return profile.halfBeam;
  if (y <= 0) return profile.waterlineHalfBeam;
  const t = (deckY - y) / deckY;
  return profile.halfBeam + (profile.waterlineHalfBeam - profile.halfBeam) * t;
}

/** Outward hull-side normal (port = -Z, starboard = +Z) at the tumblehome. */
function hullSideNormal(profile: HullStation, zSign: number): THREE.Vector3 {
  const delta = profile.waterlineHalfBeam - profile.halfBeam;
  const length = Math.hypot(delta, profile.deckY) || 1;
  return new THREE.Vector3(0, delta / length, (zSign * profile.deckY) / length);
}

/** Euler that orients a box's +Z thickness onto an outward hull normal. */
function outwardEuler(normal: THREE.Vector3): THREE.Euler {
  const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
  return new THREE.Euler().setFromQuaternion(quaternion);
}

/**
 * Build the lofted hull: sheer, solid bulwarks, tumblehome and a transom cap.
 * Each frame is an open strip traced from the port gunwale down around the
 * bilge and back up to the starboard gunwale; quads between neighbouring frames
 * form a single watertight-looking skin (one draw call).
 */
function buildHullGeometry(): THREE.BufferGeometry {
  const frames: THREE.Vector3[][] = HULL_STATIONS.map((station) => hullFramePoints(station));
  const frameCount = frames.length;
  const pointsPerFrame = frames[0].length;

  const vertices: number[] = [];
  const uvs: number[] = [];
  for (let i = 0; i < frameCount; i += 1) {
    for (let j = 0; j < pointsPerFrame; j += 1) {
      const point = frames[i][j];
      vertices.push(point.x, point.y, point.z);
      uvs.push(point.x * 0.4, j / (pointsPerFrame - 1));
    }
  }

  const indices: number[] = [];
  for (let i = 0; i < frameCount - 1; i += 1) {
    for (let j = 0; j < pointsPerFrame - 1; j += 1) {
      const a = i * pointsPerFrame + j;
      const b = a + 1;
      const c = (i + 1) * pointsPerFrame + j;
      const d = c + 1;
      indices.push(a, c, b);
      indices.push(b, c, d);
    }
  }

  // Cap the open transom with a fan from its centroid.
  const stern = frames[0];
  const centerIndex = vertices.length / 3;
  let midY = 0;
  for (const point of stern) midY += point.y;
  midY /= stern.length;
  vertices.push(HULL_STATIONS[0].x, midY, 0);
  uvs.push(HULL_STATIONS[0].x * 0.4, 0.5);
  for (let j = 0; j < pointsPerFrame - 1; j += 1) {
    indices.push(centerIndex, j, j + 1);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Trace one hull cross-section from the port gunwale to the starboard gunwale. */
function hullFramePoints(s: HullStation): THREE.Vector3[] {
  const hb = s.halfBeam;
  const wb = s.waterlineHalfBeam;
  const kb = s.keelHalfBeam;
  return [
    new THREE.Vector3(s.x, s.gunwaleY, -hb),
    new THREE.Vector3(s.x, s.deckY, -hb),
    new THREE.Vector3(s.x, s.deckY * 0.72, -(hb + (wb - hb) * 0.55)),
    new THREE.Vector3(s.x, 0, -wb),
    new THREE.Vector3(s.x, s.keelY * 0.45, -(wb + kb) * 0.5),
    new THREE.Vector3(s.x, s.keelY, -kb),
    new THREE.Vector3(s.x, s.keelY, kb),
    new THREE.Vector3(s.x, s.keelY * 0.45, (wb + kb) * 0.5),
    new THREE.Vector3(s.x, 0, wb),
    new THREE.Vector3(s.x, s.deckY * 0.72, hb + (wb - hb) * 0.55),
    new THREE.Vector3(s.x, s.deckY, hb),
    new THREE.Vector3(s.x, s.gunwaleY, hb),
  ];
}

/**
 * Build the shaped deck slab. It follows the hull planform (tapered bow and
 * transom stern) while staying flat on top at y = 1.875, so station anchors and
 * the player walk height are unchanged. A manual slab (top + bottom + sides)
 * keeps the orientation and shading unambiguous.
 */
function buildDeckGeometry(): THREE.BufferGeometry {
  const segments = 32;
  const xMin = HULL_STATIONS[0].x;
  const xMax = HULL_STATIONS[HULL_STATIONS.length - 1].x;
  const topY = 1.875;
  const bottomY = 1.625;

  // Closed outline: port edge stern->bow, then starboard edge bow->stern.
  const outline: Array<[number, number]> = [];
  for (let i = 0; i <= segments; i += 1) {
    const x = xMin + (i / segments) * (xMax - xMin);
    outline.push([x, -hullProfileAt(x).halfBeam]);
  }
  for (let i = segments; i >= 0; i -= 1) {
    const x = xMin + (i / segments) * (xMax - xMin);
    outline.push([x, hullProfileAt(x).halfBeam]);
  }
  const count = outline.length;

  const vertices: number[] = [];
  const indices: number[] = [];

  const topBase = 0;
  for (const [x, z] of outline) vertices.push(x, topY, z);
  const bottomBase = count;
  for (const [x, z] of outline) vertices.push(x, bottomY, z);

  // Top face fan (upward normal).
  const topCenter = vertices.length / 3;
  vertices.push(1.0, topY, 0);
  for (let i = 0; i < count; i += 1) {
    indices.push(topCenter, topBase + ((i + 1) % count), topBase + i);
  }

  // Bottom face fan (downward normal).
  const bottomCenter = vertices.length / 3;
  vertices.push(1.0, bottomY, 0);
  for (let i = 0; i < count; i += 1) {
    indices.push(bottomCenter, bottomBase + i, bottomBase + ((i + 1) % count));
  }

  // Side faces (quad strip between the two rings).
  for (let i = 0; i < count; i += 1) {
    const j = (i + 1) % count;
    const a = topBase + i;
    const b = topBase + j;
    const c = bottomBase + i;
    const d = bottomBase + j;
    indices.push(a, b, d);
    indices.push(a, d, c);
  }

  // Planar plank UVs: u follows the ship length, v follows the beam.
  const uvs: number[] = [];
  for (let i = 0; i < vertices.length; i += 3) {
    uvs.push(vertices[i] * 0.5, vertices[i + 2] * 0.5);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Build a single-panel square sail with a leeward belly. The head (top) stays
 * flatter and the belly peaks about a third of the way up from the foot, like a
 * real sail under wind. Local +Z is the leeward direction; the ship rotates the
 * sail so it curves forward before applying the authoritative boom angle.
 */
function buildBillowingSailGeometry(width: number, height: number): THREE.BufferGeometry {
  const segW = 8;
  const segH = 10;
  const vertices: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let iy = 0; iy <= segH; iy += 1) {
    const ty = iy / segH;
    for (let ix = 0; ix <= segW; ix += 1) {
      const tx = ix / segW;
      const x = (tx - 0.5) * width;
      const y = (ty - 0.5) * height;
      // Full-width curve, peaked low on the sail (ty ~ 0.35).
      const belly = Math.sin(tx * Math.PI) * Math.sin(Math.PI * Math.pow(ty, 0.55));
      vertices.push(x, y, belly);
      uvs.push(tx, ty);
    }
  }

  for (let iy = 0; iy < segH; iy += 1) {
    for (let ix = 0; ix < segW; ix += 1) {
      const a = iy * (segW + 1) + ix;
      const b = a + 1;
      const c = a + (segW + 1);
      const d = c + 1;
      indices.push(a, c, b);
      indices.push(b, c, d);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Helm wheel: rim + six spokes baked into one geometry (single draw call). */
function buildWheelGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [new THREE.TorusGeometry(0.5, 0.07, 8, 20)];
  const spoke = new THREE.CylinderGeometry(0.03, 0.03, 0.9, 4);
  for (let s = 0; s < 6; s += 1) {
    const spokeGeometry = spoke.clone();
    spokeGeometry.applyMatrix4(new THREE.Matrix4().makeRotationZ((s / 6) * Math.PI));
    parts.push(spokeGeometry);
  }
  spoke.dispose();
  const merged = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  if (!merged) throw new Error('Ship: failed to merge the helm wheel geometry.');
  return merged;
}

/** Procedural low-poly ship whose sails reflect authoritative level/angle. */
export class Ship {
  readonly group = new THREE.Group();

  private readonly sailGroups: THREE.Group[] = [];
  private readonly sailMeshes: THREE.Mesh[] = [];
  private readonly sailBasePositions: Float32Array[] = [];
  private readonly sailLevelFractions: number[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly instancedMeshes: THREE.InstancedMesh[] = [];
  private readonly staticPartSets = new Map<THREE.Material, StaticPartSet>();
  private readonly library: ModelLibrary;
  private readonly ownsLibrary: boolean;
  private readonly glbPropMeshes: THREE.InstancedMesh[] = [];
  private propLoadStarted = false;
  private disposed = false;
  /** Procedural fallback ship group (hull, deck, cabin, masts, sails, rigging, trim). */
  private readonly proceduralGroup = new THREE.Group();
  /** Realistic hero-hull GLB shell (Poly Haven dutch_ship_medium), when loaded. */
  private heroHullGroup: THREE.Group | null = null;
  private heroHullLoadStarted = false;
  private readonly lanternMaterials: THREE.MeshStandardMaterial[] = [];
  private readonly flagGroup = new THREE.Group();
  private wheelMesh: THREE.Mesh | null = null;
  private flagMesh: THREE.Mesh | null = null;
  private flagGeometry: THREE.PlaneGeometry | null = null;
  private flagBasePositions: Float32Array | null = null;
  private flagWidth = 0;
  private flagHeight = 0;
  private flagHeading = Math.PI; // streams aft while becalmed
  private flagTime = 0;
  private windStrength = 0;
  private clothWindStrength = 0;
  private windDirection = 0;
  private markerMesh: THREE.InstancedMesh | null = null;
  private readonly stationMarkerFreeColors: number[] = [];
  private heelAngle = 0;
  private markerTime = 0;
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpVec = new THREE.Vector3();
  private readonly tmpClothQuat = new THREE.Quaternion();
  private readonly clothSails: Array<SailCloth | null> = [];
  private readonly clothOption: ClothOption;
  private readonly highDetail: boolean;
  private lodObserver: THREE.Vector3 | null = null;

  constructor(
    sailCount: number,
    palette: ShipPalette = {},
    detail: ShipDetail = 'high',
    library?: ModelLibrary,
    cloth: ClothOption = 'off',
  ) {
    this.group.name = 'ship';
    this.proceduralGroup.name = 'procedural-ship';
    this.group.add(this.proceduralGroup);
    this.clothOption = cloth;
    this.highDetail = detail === 'high';
    this.library = library ?? new ModelLibrary();
    this.ownsLibrary = !library;

    const colors: Required<ShipPalette> = { ...DEFAULT_PALETTE, ...palette };
    const materialSet = createShipMaterials({
      hull: colors.hull,
      deck: colors.deck,
      trim: colors.trim,
      sail: colors.sail,
      mast: colors.mast,
      helm: colors.helm,
      sailStation: colors.sailStation,
      railing: RAILING_COLOR,
      window: WINDOW_COLOR,
      rope: ROPE_COLOR,
      plankDark: PLANK_DARK,
    });
    const hullMaterial = this.material(materialSet.hull);
    const deckMaterial = this.material(materialSet.deck);
    const trimMaterial = this.material(materialSet.trim);
    const mastMaterial = this.material(materialSet.mast);
    const sailMaterial = this.material(materialSet.sail);
    const windowMaterial = this.material(materialSet.window);
    const ropeMaterial = this.material(materialSet.rope);

    // Dedicated Black-Sails dressing materials.
    const lanternHousingMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: LANTERN_HOUSING_COLOR,
        roughness: 0.45,
        metalness: 0.6,
        envMapIntensity: 0.6,
      }),
    );
    const lanternGlowMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: LANTERN_GLOW_COLOR,
        emissive: LANTERN_GLOW_COLOR,
        emissiveIntensity: 2.2,
        roughness: 0.4,
        metalness: 0,
        toneMapped: false,
      }),
    );
    this.lanternMaterials.push(lanternGlowMaterial);
    const flagMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: FLAG_COLOR,
        roughness: 0.9,
        metalness: 0,
        side: THREE.DoubleSide,
        envMapIntensity: 0.2,
      }),
    );

    // ── Hull ──
    hullMaterial.side = THREE.DoubleSide; // bulwark inner faces stay visible from above
    // Planking + weathering detail map is shared by every ship (no per-vessel
    // texture duplication) and is assigned inside `createShipMaterials`.
    //
    // The realistic hero hull (Poly Haven dutch_ship_medium) is adopted as an
    // overlaid replacement shell on top of this procedural hull; see
    // `preloadHeroHull()`/`buildHeroHull()`. This procedural hull stays as the
    // never-throws fallback and is hidden only after the GLB actually lands.
    const hull = new THREE.Mesh(this.geometry(buildHullGeometry()), hullMaterial);
    this.shadow(hull);
    this.proceduralGroup.add(hull);

    // ── Deck + stern castle (merged into one shadow-casting mesh) ──
    this.addStaticPart(
      buildDeckGeometry(),
      deckMaterial,
      new THREE.Vector3(0, 0, 0),
      undefined,
      undefined,
      true,
    );
    this.addStaticPart(
      new THREE.BoxGeometry(3.6, 2.0, 4.6),
      deckMaterial,
      new THREE.Vector3(-3.8, 2.875, 0),
      undefined,
      undefined,
      true,
    );

    // ── Trim parts (merged into one non-shadow-casting decorative mesh) ──
    const trimPart = (
      geometry: THREE.BufferGeometry,
      position: THREE.Vector3,
      rotation?: THREE.Euler,
      scale?: THREE.Vector3,
    ): void => this.addStaticPart(geometry, trimMaterial, position, rotation, scale, false);

    trimPart(new THREE.BoxGeometry(4.0, 0.14, 5.0), new THREE.Vector3(-3.8, 3.95, 0)); // poop roof
    trimPart(
      new THREE.PlaneGeometry(0.7, 1.3),
      new THREE.Vector3(-1.99, 2.55, 0),
      new THREE.Euler(0, -Math.PI / 2, 0),
    ); // cabin door
    trimPart(new THREE.BoxGeometry(0.08, 0.08, 0.75), new THREE.Vector3(-1.98, 3.22, 0)); // door frame
    trimPart(new THREE.BoxGeometry(0.12, 1.8, 0.8), new THREE.Vector3(-8.2, -0.1, 0)); // rudder

    if (detail === 'high') {
      // Figurehead: a cone whose tip points forward (+X, bow).
      trimPart(
        new THREE.ConeGeometry(0.24, 0.8, 6),
        new THREE.Vector3(11.4, 1.5, 0),
        new THREE.Euler(0, 0, -Math.PI / 2),
      );
      // Anchor shank + stock.
      trimPart(new THREE.CylinderGeometry(0.05, 0.07, 1.1, 6), new THREE.Vector3(9.6, 1.0, 2.15));
      trimPart(new THREE.BoxGeometry(0.7, 0.06, 0.06), new THREE.Vector3(9.6, 1.5, 2.15));
      // Anchor flukes.
      trimPart(
        new THREE.BoxGeometry(0.35, 0.05, 0.16),
        new THREE.Vector3(9.6, 0.45, 2.3),
        new THREE.Euler(0.5, 0, 0),
      );
      trimPart(
        new THREE.BoxGeometry(0.35, 0.05, 0.16),
        new THREE.Vector3(9.6, 0.45, 2.0),
        new THREE.Euler(-0.5, 0, 0),
      );
      // Capstan drum.
      trimPart(new THREE.CylinderGeometry(0.4, 0.48, 0.7, 8), new THREE.Vector3(1.4, 2.1, 0));
      // Stern name plate.
      trimPart(new THREE.BoxGeometry(0.08, 0.6, 3.0), new THREE.Vector3(-8.62, 1.2, 0));
      // Bulwark cap-rail segments riding the gunwale sheer.
      const capRailCount = 14;
      for (let i = 0; i < capRailCount; i += 1) {
        const x =
          HULL_STATIONS[0].x +
          (i / (capRailCount - 1)) * (HULL_STATIONS[HULL_STATIONS.length - 1].x - HULL_STATIONS[0].x);
        const profile = hullProfileAt(x);
        for (const zSign of [-1, 1]) {
          trimPart(
            new THREE.BoxGeometry(0.55, 0.08, 0.1),
            new THREE.Vector3(x, profile.gunwaleY, zSign * profile.halfBeam),
          );
        }
      }
    }

    // ── Mast-material parts (merged into one shadow-casting mesh) ──
    const mastPart = (
      geometry: THREE.BufferGeometry,
      position: THREE.Vector3,
      rotation?: THREE.Euler,
      scale?: THREE.Vector3,
    ): void => this.addStaticPart(geometry, mastMaterial, position, rotation, scale, true);

    // Bowsprit (angled upward).
    mastPart(
      new THREE.CylinderGeometry(0.08, 0.12, 5, 6),
      new THREE.Vector3(12.5, 2.2, 0),
      new THREE.Euler(0, 0, -Math.PI / 2 + 0.25),
    );
    // Stern flag pole.
    mastPart(new THREE.CylinderGeometry(0.03, 0.04, 2.2, 5), new THREE.Vector3(-7.6, 3.0, 0));
    if (detail === 'high') {
      mastPart(new THREE.SphereGeometry(0.13, 6, 4), new THREE.Vector3(11.8, 1.56, 0)); // figurehead head
    }

    // ── Masts, yards, sails, and rigging ──
    const mastHeights = [11, 9.5]; // main mast taller than fore
    const riggingSegments: Array<readonly [THREE.Vector3, THREE.Vector3]> = [];

    for (let i = 0; i < sailCount; i += 1) {
      const x = MAST_X[i % MAST_X.length] + (i >= MAST_X.length ? 2.0 : 0);
      const mastHeight = mastHeights[i % mastHeights.length] ?? 11;
      const mastTopY = 1.88 + mastHeight;

      // Mast, top cap and crow's nest fold into the merged mast mesh.
      mastPart(
        new THREE.CylinderGeometry(0.12, 0.22, mastHeight, 8),
        new THREE.Vector3(x, 1.88 + mastHeight / 2, 0),
      );
      mastPart(new THREE.SphereGeometry(0.15, 6, 4), new THREE.Vector3(x, mastTopY + 0.1, 0));
      mastPart(
        new THREE.CylinderGeometry(0.6, 0.5, 0.15, 8),
        new THREE.Vector3(x, 1.88 + mastHeight * 0.75, 0),
      );

      // Yards (two per mast) fold into the merged mast mesh too.
      const yardCount = 2;
      for (let yi = 0; yi < yardCount; yi++) {
        const yardY = 1.88 + mastHeight * (0.45 + yi * 0.3);
        const yardWidth = 5.5 - yi * 0.8;
        mastPart(
          new THREE.CylinderGeometry(0.05, 0.07, yardWidth, 6),
          new THREE.Vector3(x, yardY, 0),
          new THREE.Euler(Math.PI / 2, 0, 0),
        );

        if (detail === 'high') {
          // Running rigging: lifts from each yard arm up to the mast head.
          for (const zSign of [-1, 1]) {
            riggingSegments.push([
              new THREE.Vector3(x, yardY, zSign * yardWidth * 0.5),
              new THREE.Vector3(x, mastTopY, 0),
            ]);
          }
          // Braces from the lower yard arms down to the deck.
          if (yi === 0) {
            for (const zSign of [-1, 1]) {
              riggingSegments.push([
                new THREE.Vector3(x, yardY, zSign * yardWidth * 0.5),
                new THREE.Vector3(x - 2.0, 1.9, zSign * 2.1),
              ]);
            }
          }
        }
      }

      // Sail group (kept as its own mesh so it can billow and rotate).
      const sailGroup = new THREE.Group();
      const sailY = 1.88 + mastHeight * 0.45;
      sailGroup.position.set(x, sailY, 0);
      this.proceduralGroup.add(sailGroup);

      const sailHeight = mastHeight * 0.35;
      const sailWidth = 4.8 - i * 0.4;
      let sail: THREE.Mesh;
      let clothSail: SailCloth | null = null;

      if (this.clothOption !== 'off') {
        const clothQuality: ClothQuality = this.clothOption;
        clothSail = new SailCloth(sailWidth, sailHeight, {
          quality: clothQuality,
          material: sailMaterial,
        });
        clothSail.mesh.rotation.y = Math.PI / 2;
        clothSail.mesh.position.set(0, 0.4, 0);
        sail = clothSail.mesh;
      } else {
        const sailGeo = this.geometry(buildBillowingSailGeometry(sailWidth, sailHeight));
        sail = new THREE.Mesh(sailGeo, sailMaterial);
        sail.rotation.y = Math.PI / 2;
        sail.position.set(0, 0.4, 0);
        this.sailBasePositions.push(
          (sailGeo.getAttribute('position') as THREE.BufferAttribute).array.slice(0) as Float32Array,
        );
      }
      this.shadow(sail);
      sailGroup.add(sail);

      this.sailGroups.push(sailGroup);
      this.sailMeshes.push(sail);
      this.sailLevelFractions.push(0);
      this.clothSails.push(clothSail);

      // Shrouds (mast top to hull sides).
      for (const zSign of [-1, 1]) {
        riggingSegments.push([
          new THREE.Vector3(x, mastTopY, 0),
          new THREE.Vector3(x, 1.9, zSign * 2.8),
        ]);
      }

      // Ratlines: rope-ladder rungs climbing the shrouds (high detail only).
      if (detail === 'high') {
        const rungCount = 6;
        const rungTop = 1.9 + mastHeight * 0.62;
        for (let r = 0; r < rungCount; r += 1) {
          const rungY = 2.0 + ((rungTop - 2.0) * r) / (rungCount - 1);
          for (const zSign of [-1, 1]) {
            riggingSegments.push([
              new THREE.Vector3(x, rungY, zSign * 0.12),
              new THREE.Vector3(x, rungY, zSign * 1.55),
            ]);
          }
        }
      }

      // Forestay / backstay lines.
      if (i === 0) {
        riggingSegments.push([new THREE.Vector3(x, mastTopY, 0), new THREE.Vector3(-7, 2.5, 0)]);
      }
      if (i === sailCount - 1) {
        riggingSegments.push([new THREE.Vector3(x, mastTopY, 0), new THREE.Vector3(12, 2.5, 0)]);
      }
    }

    // Capstan bars (high detail only), folded into the merged mast mesh.
    if (detail === 'high') {
      for (let s = 0; s < 4; s++) {
        const angle = (s / 4) * Math.PI * 2 + Math.PI / 4;
        mastPart(
          new THREE.CylinderGeometry(0.035, 0.035, 1.0, 4),
          new THREE.Vector3(1.4 + Math.cos(angle) * 0.55, 2.2, Math.sin(angle) * 0.55),
          new THREE.Euler(0, angle, 0),
        );
      }
    }

    // Bowsprit footropes (high detail only), drawn as rigging lines.
    if (detail === 'high') {
      const footropeLength = Math.sqrt(4.5 * 4.5 + 1.2 * 1.2 + 2.2 * 2.2);
      const footropeTilt = Math.atan2(2.4, footropeLength) * 0.4;
      for (const zSign of [-1, 1]) {
        const direction = new THREE.Vector3(0, Math.cos(footropeTilt), zSign * Math.sin(footropeTilt));
        const center = new THREE.Vector3(11.3, 1.75, zSign * 1.2);
        const half = direction.multiplyScalar(footropeLength * 0.5);
        riggingSegments.push([center.clone().sub(half), center.clone().add(half)]);
      }
    }

    // Helm wheel (low detail): baked into the trim mesh as a static prop so the
    // mobile profile saves the wheel + spokes draw calls. High detail keeps a
    // separate rotating wheel below.
    if (!this.highDetail) {
      this.addStaticPart(
        buildWheelGeometry(),
        trimMaterial,
        new THREE.Vector3(STATION_ANCHORS[0].x, 2.45, STATION_ANCHORS[0].z),
        undefined,
        undefined,
        false,
      );
    }

    // Bake the collected static parts into one mesh per material.
    this.buildMergedParts();

    // Rigging: one instanced cylinder set for every rope line. Skipped on the
    // mobile profile (thin shrouds/stays cost a draw call there).
    if (this.highDetail) this.addLineSet(riggingSegments, ropeMaterial, 0.017);

    if (this.highDetail) {
      // ── Cabin windows (instanced, facing outward) ──
      const windowTransforms: InstanceTransform[] = [
        { position: new THREE.Vector3(-4.3, 3.0, -2.32), rotation: new THREE.Euler(0, Math.PI, 0) },
        { position: new THREE.Vector3(-3.1, 3.0, -2.32), rotation: new THREE.Euler(0, Math.PI, 0) },
        { position: new THREE.Vector3(-4.3, 3.0, 2.32) },
        { position: new THREE.Vector3(-3.1, 3.0, 2.32) },
        { position: new THREE.Vector3(-5.62, 3.0, -1.2), rotation: new THREE.Euler(0, -Math.PI / 2, 0) },
        { position: new THREE.Vector3(-5.62, 3.0, 0), rotation: new THREE.Euler(0, -Math.PI / 2, 0) },
        { position: new THREE.Vector3(-5.62, 3.0, 1.2), rotation: new THREE.Euler(0, -Math.PI / 2, 0) },
      ];
      this.proceduralGroup.add(
        this.instanced(
          this.geometry(new THREE.PlaneGeometry(0.6, 0.5)),
          windowMaterial,
          windowTransforms,
          false,
        ),
      );
    }

    // ── Hull detail: wales, gun ports and a hint of a bow wave ──
    if (this.highDetail) this.addHullDetail(trimMaterial);

    if (this.highDetail) {
      // ── Lanterns (warm emissive glow; desktop-only dressing) ──
      const lanternPositions: Array<[number, number, number]> = [
        [-6.2, 2.25, -2.5],
        [-6.2, 2.25, 2.5],
        [7.5, 2.4, -1.6],
        [7.5, 2.4, 1.6],
      ];
      const lanternHousingTransforms: InstanceTransform[] = lanternPositions.map(
        (pos) => ({ position: new THREE.Vector3(pos[0], pos[1], pos[2]) }),
      );
      this.proceduralGroup.add(
        this.instanced(
          this.geometry(new THREE.BoxGeometry(0.3, 0.36, 0.3)),
          lanternHousingMaterial,
          lanternHousingTransforms,
          false,
        ),
      );
      const lanternGlowTransforms: InstanceTransform[] = lanternPositions.map(
        (pos) => ({ position: new THREE.Vector3(pos[0], pos[1] - 0.02, pos[2]) }),
      );
      this.proceduralGroup.add(
        this.instanced(
          this.geometry(new THREE.SphereGeometry(0.1, 6, 4)),
          lanternGlowMaterial,
          lanternGlowTransforms,
          false,
        ),
      );
    }

    if (this.highDetail) {
      // ── Wind-driven flag at the stern ──
      this.flagGroup.position.set(-7.6, 4.05, 0);
      this.proceduralGroup.add(this.flagGroup);

      this.flagWidth = 1.7;
      this.flagHeight = 0.5;
      const flagSegW = 10;
      const flagSegH = 2;
      this.flagGeometry = this.geometry(
        new THREE.PlaneGeometry(this.flagWidth, this.flagHeight, flagSegW, flagSegH),
      );
      // Anchor the flag at the pole (local +X points downwind from the pole).
      this.flagGeometry.translate(this.flagWidth / 2, 0, 0);
      this.flagMesh = new THREE.Mesh(this.flagGeometry, flagMaterial);
      this.flagMesh.castShadow = false;
      this.flagMesh.receiveShadow = true;
      this.flagGroup.add(this.flagMesh);
      this.flagBasePositions = (
        this.flagGeometry.getAttribute('position') as THREE.BufferAttribute
      ).array.slice(0) as Float32Array;
    }

    if (detail === 'high') void this.preloadProps();
    if (detail === 'high') void this.preloadHeroHull();

    // ── Station markers: small, faint deck inlays (one instanced draw call) ──
    const markerMaterial = this.material(
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: MARKER_OPACITY,
        depthWrite: false,
      }),
    );
    const markerTransforms: InstanceTransform[] = [];
    for (let i = 0; i < STATION_ANCHORS.length; i += 1) {
      const anchor = STATION_ANCHORS[i];
      this.stationMarkerFreeColors.push(i === 0 ? colors.helm : colors.sailStation);
      markerTransforms.push({
        position: new THREE.Vector3(anchor.x, 1.905, anchor.z),
        rotation: new THREE.Euler(-Math.PI / 2, 0, 0),
      });
    }
    const markerMesh = this.instanced(
      this.geometry(new THREE.RingGeometry(MARKER_INNER_RADIUS, MARKER_OUTER_RADIUS, MARKER_SEGMENTS)),
      markerMaterial,
      markerTransforms,
      false,
    );
    this.markerMesh = markerMesh;
    for (let i = 0; i < this.stationMarkerFreeColors.length; i += 1) {
      markerMesh.setColorAt(
        i,
        new THREE.Color(this.stationMarkerFreeColors[i]).multiplyScalar(0.6),
      );
    }
    if (markerMesh.instanceColor) markerMesh.instanceColor.needsUpdate = true;

    if (this.highDetail) {
      // Helm wheel: a real wood prop (rim + spokes in one rotating mesh).
      const wheelMat = this.material(
        new THREE.MeshStandardMaterial({ color: colors.trim, roughness: 0.55, metalness: 0.06 }),
      );
      const wheel = new THREE.Mesh(this.geometry(buildWheelGeometry()), wheelMat);
      wheel.position.set(STATION_ANCHORS[0].x, 2.45, STATION_ANCHORS[0].z);
      wheel.castShadow = false;
      wheel.receiveShadow = true;
      this.proceduralGroup.add(wheel);
      this.wheelMesh = wheel;
    }
  }

  stationAnchor(index: number): THREE.Vector3 {
    return STATION_ANCHORS[index] ?? new THREE.Vector3();
  }

  /**
   * Advance wind-driven dressing (flag flutter, lantern flicker, sail billow).
   * Call after `setPose` so the flag can resolve world wind against the hull
   * heading. If this method is never called the ship renders with its static
   * becalmed pose and `updateSails` keeps its existing level-based behaviour.
   */
  update(delta: number, windDirection: number, windStrength: number): void {
    const dt = Number.isFinite(delta) ? delta : 0;
    this.windStrength = Number.isFinite(windStrength) ? Math.min(1, Math.max(0, windStrength)) : 0;
    this.clothWindStrength = Number.isFinite(windStrength) ? Math.min(1.5, Math.max(0, windStrength)) : 0;
    this.windDirection = Number.isFinite(windDirection) ? windDirection : 0;
    this.flagTime += dt;

    // Stream the flag downwind in ship-local space (world -> local quaternion).
    let target = Math.PI;
    if (this.windStrength > 0.02) {
      const worldWindX = Math.cos(this.windDirection);
      const worldWindZ = -Math.sin(this.windDirection);
      this.tmpQuat.copy(this.group.quaternion).invert();
      this.tmpVec.set(worldWindX, 0, worldWindZ).applyQuaternion(this.tmpQuat);
      target = Math.atan2(this.tmpVec.z, this.tmpVec.x);
    }
    let diff = target - this.flagHeading;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    this.flagHeading += diff * Math.min(1, 4 * dt);
    this.flagGroup.rotation.y = this.flagHeading;

    this.writeFlagVertices();
    this.flickerLanterns();
    this.updateSailCloth(dt);
    this.updateMarkerVisibility();
  }

  /** Apply authoritative sail levels/angles to the visible sails. */
  updateSails(sails: Iterable<SailLike>): void {
    let index = 0;
    for (const sail of sails) {
      const group = this.sailGroups[index];
      const mesh = this.sailMeshes[index];
      if (group && mesh) {
        group.rotation.y = sail.angle;
        const fraction = Math.max(0, Math.min(1, sail.level / 4));
        this.sailLevelFractions[index] = fraction;
        mesh.visible = sail.level > 0;
        mesh.scale.set(1, 0.25 + 0.75 * fraction, 1);
        mesh.position.y = 0.4 - (1 - mesh.scale.y) * 1.4;
        this.clothSails[index]?.setLevel(sail.level);
      }
      index += 1;
    }
    for (; index < this.sailMeshes.length; index += 1) {
      this.sailMeshes[index].visible = false;
      this.sailLevelFractions[index] = 0;
    }
  }

  /** Advance simulated cloth sails; static sails keep the old morph path. */
  private updateSailCloth(delta: number): void {
    const distance = this.lodDistance();
    for (let i = 0; i < this.sailMeshes.length; i += 1) {
      const cloth = this.clothSails[i];
      if (!cloth) {
        this.morphSail(i, this.flagTime);
        continue;
      }
      const group = this.sailGroups[i];
      const mesh = this.sailMeshes[i];
      if (group && mesh) {
        // Sail-mesh world quaternion = ship * sailGroup * mesh-local rotation.
        this.tmpClothQuat
          .copy(this.group.quaternion)
          .multiply(group.quaternion)
          .multiply(mesh.quaternion);
        cloth.setWorldQuaternion(this.tmpClothQuat);
      }
      cloth.setWind(this.windDirection, this.clothWindStrength);
      cloth.update(delta, distance);
    }
  }

  /** Camera distance for cloth LOD, or undefined when no observer is set. */
  private lodDistance(): number | undefined {
    if (!this.lodObserver) return undefined;
    return this.lodObserver.distanceTo(this.group.position);
  }

  /** Deform one sail's vertices: belly scales with level/wind plus a flutter. */
  private morphSail(index: number, time: number): void {
    const mesh = this.sailMeshes[index];
    const base = this.sailBasePositions[index];
    if (!mesh || !base) return;
    const fraction = this.sailLevelFractions[index] ?? 0;
    const wind = this.windStrength;
    // Furled sails sit flat; full sails belly out. Wind adds a lively ripple.
    const billow = 0.4 * fraction + 0.6 * fraction * fraction + wind * 0.6 * fraction;
    const position = mesh.geometry.attributes.position as THREE.BufferAttribute;
    const array = position.array as Float32Array;
    for (let i = 0; i < position.count; i += 1) {
      const x = array[i * 3];
      const y = array[i * 3 + 1];
      const belly = base[i * 3 + 2];
      const flutter = 0.035 * wind * fraction * Math.sin(time * 2.4 + x * 0.7 + y * 0.9);
      array[i * 3 + 2] = belly * billow + flutter;
    }
    position.needsUpdate = true;
    mesh.geometry.computeVertexNormals();
  }

  /** Optional camera position used for cloth LOD (pass null to disable). */
  setLodObserver(position: THREE.Vector3 | null): void {
    this.lodObserver = position;
  }

  /** Hide the station markers when the camera is too far away to read them. */
  private updateMarkerVisibility(): void {
    if (!this.markerMesh) return;
    const distance = this.lodDistance();
    this.markerMesh.visible = distance === undefined || distance <= MARKER_HIDE_DISTANCE;
  }

  /** Update station marker colours: faint accent when free, muted when occupied. */
  updateStationMarkers(occupied: boolean[], delta: number): void {
    this.markerTime += delta;
    const mesh = this.markerMesh;
    if (!mesh) return;
    // Subtle slow pulse keeps the cue alive without a bright glow.
    const pulse = 0.9 + 0.1 * Math.sin(this.markerTime * 1.6);
    const free = new THREE.Color();
    const muted = new THREE.Color(0x5a5a52);
    for (let i = 0; i < this.stationMarkerFreeColors.length; i += 1) {
      if (occupied[i]) {
        mesh.setColorAt(i, muted);
      } else {
        free.setHex(this.stationMarkerFreeColors[i]).multiplyScalar(0.5 * pulse);
        mesh.setColorAt(i, free);
      }
    }
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    this.updateMarkerVisibility();
  }

  setPose(position: THREE.Vector3, quaternion: THREE.Quaternion): void {
    this.group.position.copy(position);
    this.group.quaternion.copy(quaternion);
  }

  /** Apply visual-only heel (roll) and wheel rotation based on rudder + speed. */
  setTurnHeel(rudder: number, speed: number, delta: number): void {
    const MAX_HEEL = 0.14; // ~8 degrees
    const MAX_WHEEL = Math.PI * 1.5; // 270 degrees full lock
    const LERP_RATE = 4;
    const targetHeel = -rudder * (speed / 9) * MAX_HEEL;
    this.heelAngle += (targetHeel - this.heelAngle) * Math.min(1, LERP_RATE * delta);
    this.group.rotateZ(this.heelAngle);
    if (this.wheelMesh) this.wheelMesh.rotation.y = rudder * MAX_WHEEL;
  }

  /** Flutter the flag's vertices in place (high detail only). */
  private writeFlagVertices(): void {
    if (!this.flagGeometry || !this.flagBasePositions || !this.flagMesh) return;
    const position = this.flagGeometry.attributes.position as THREE.BufferAttribute;
    const base = this.flagBasePositions;
    const width = this.flagWidth || 1;
    const amp = 0.02 + 0.16 * this.windStrength;
    const freq = 5 + 16 * this.windStrength;
    const t = this.flagTime;
    for (let i = 0; i < position.count; i += 1) {
      const u = Math.min(1, Math.max(0, base[i * 3] / width));
      const wave =
        Math.sin(u * Math.PI * 3 - t * freq) +
        0.5 * Math.sin(u * Math.PI * 5 - t * freq * 1.6 + 1.2);
      position.setZ(i, amp * wave * u);
    }
    position.needsUpdate = true;
    this.flagGeometry.computeVertexNormals();
  }

  /** Flicker the lantern glow with wind (storm = brighter, livelier). */
  private flickerLanterns(): void {
    if (this.lanternMaterials.length === 0) return;
    const t = this.flagTime;
    const flicker = 0.85 + 0.15 * Math.sin(t * 9) + 0.08 * Math.sin(t * 23.7 + 1.7);
    const intensity = 2.0 * flicker * (0.6 + 0.4 * this.windStrength);
    for (const material of this.lanternMaterials) {
      material.emissiveIntensity = intensity;
    }
  }

  /** Create a static instanced mesh and register it for disposal/shadowing. */
  private instanced(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    transforms: InstanceTransform[],
    castShadow = true,
  ): THREE.InstancedMesh {
    const template = new THREE.Mesh(geometry, material);
    template.castShadow = castShadow;
    template.receiveShadow = true;
    const mesh = instanceStatic(template, transforms);
    this.instancedMeshes.push(mesh);
    return mesh;
  }

  /** Build an instanced set of thin rope lines from endpoint pairs. */
  private addLineSet(
    segments: ReadonlyArray<readonly [THREE.Vector3, THREE.Vector3]>,
    material: THREE.Material,
    radius: number,
    castShadow = false,
  ): void {
    if (segments.length === 0) return;
    const geometry = this.geometry(new THREE.CylinderGeometry(radius, radius, 1, 4, 1, true));
    const mesh = new THREE.InstancedMesh(geometry, material, segments.length);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = false;
    const matrix = new THREE.Matrix4();
    const dir = new THREE.Vector3();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3(1, 1, 1);
    const quaternion = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < segments.length; i += 1) {
      const [a, b] = segments[i];
      dir.subVectors(b, a);
      const length = dir.length() || 0.0001;
      dir.normalize();
      quaternion.setFromUnitVectors(up, dir);
      position.addVectors(a, b).multiplyScalar(0.5);
      scale.set(1, length, 1);
      matrix.compose(position, quaternion, scale);
      mesh.setMatrixAt(i, matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    this.instancedMeshes.push(mesh);
    this.proceduralGroup.add(mesh);
  }

  /**
   * Queue a static mesh as a part of a per-material merged geometry. Folding
   * the many one-off boxes/cylinders into one mesh per material keeps the whole
   * ship down to a handful of draw calls.
   */
  private addStaticPart(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    position: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    castShadow = false,
  ): void {
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion().setFromEuler(rotation ?? IDENTITY_EULER);
    matrix.compose(position, quaternion, scale ?? ONE_SCALE);
    let set = this.staticPartSets.get(material);
    if (!set) {
      set = { castShadow, parts: [] };
      this.staticPartSets.set(material, set);
    }
    set.parts.push({ geometry: this.geometry(geometry), matrix });
  }

  /** Merge queued static parts into one mesh per material. */
  private buildMergedParts(): void {
    for (const [material, set] of this.staticPartSets) {
      if (set.parts.length === 0) continue;
      const geometries = set.parts.map((part) => {
        const geometry = part.geometry.clone();
        geometry.applyMatrix4(part.matrix);
        return geometry;
      });
      const merged = mergeGeometries(geometries, false);
      for (const geometry of geometries) geometry.dispose();
      for (const part of set.parts) part.geometry.dispose(); // originals are now baked into the merged mesh
      if (!merged) throw new Error('Ship: failed to merge static parts into a single mesh.');
      const mesh = new THREE.Mesh(this.geometry(merged), material);
      mesh.castShadow = set.castShadow;
      mesh.receiveShadow = true;
      this.proceduralGroup.add(mesh);
    }
    this.staticPartSets.clear();
  }

  /**
   * Add the weather-beaten hull dressing that reads from the chase camera:
   * raised wale bands, a row of dark gun ports, and a foam bow wave. Each layer
   * is a single instanced mesh, so the detail is nearly free in the budget.
   */
  private addHullDetail(trimMaterial: THREE.Material): void {
    // Wales: two raised planking bands per side, hugging the tumblehome.
    const waleTransforms: InstanceTransform[] = [];
    const waleYs = [1.38, 0.74];
    const waleSegments = 24;
    for (const waleY of waleYs) {
      for (let i = 0; i < waleSegments; i += 1) {
        const x =
          HULL_STATIONS[0].x +
          (i / (waleSegments - 1)) * (HULL_STATIONS[HULL_STATIONS.length - 1].x - HULL_STATIONS[0].x);
        const profile = hullProfileAt(x);
        if (profile.halfBeam < 1.1) continue; // taper too narrow for a band
        const z = hullSideZ(profile, waleY);
        for (const zSign of [-1, 1]) {
          const normal = hullSideNormal(profile, zSign);
          waleTransforms.push({
            position: new THREE.Vector3(x, waleY, zSign * z).addScaledVector(normal, 0.04),
            rotation: outwardEuler(normal),
          });
        }
      }
    }
    this.proceduralGroup.add(
      this.instanced(
        this.geometry(new THREE.BoxGeometry(0.85, 0.11, 0.13)),
        trimMaterial,
        waleTransforms,
        false,
      ),
    );

    // Gun ports: dark squares let into the hull side between the wales.
    const gunPortTransforms: InstanceTransform[] = [];
    const gunPortXs = [-6.9, -6.3, -1, 1, 3, 5, 7];
    const gunPortY = 1.1;
    for (const x of gunPortXs) {
      const profile = hullProfileAt(x);
      if (profile.halfBeam < 0.9) continue;
      const z = hullSideZ(profile, gunPortY);
      for (const zSign of [-1, 1]) {
        const normal = hullSideNormal(profile, zSign);
        gunPortTransforms.push({
          position: new THREE.Vector3(x, gunPortY, zSign * z).addScaledVector(normal, 0.02),
          rotation: outwardEuler(normal),
        });
      }
    }
    const gunPortMaterial = this.material(
      new THREE.MeshStandardMaterial({ color: 0x0c0805, roughness: 0.6, metalness: 0.05 }),
    );
    this.proceduralGroup.add(
      this.instanced(
        this.geometry(new THREE.BoxGeometry(0.3, 0.32, 0.07)),
        gunPortMaterial,
        gunPortTransforms,
        false,
      ),
    );

    // Bow wave: foam quads flaring aft from the cutwater.
    const foamGeometry = this.geometry(new THREE.PlaneGeometry(1, 1));
    foamGeometry.rotateX(-Math.PI / 2);
    const foamTransforms: InstanceTransform[] = [];
    const foamSegments = 8;
    for (let i = 0; i < foamSegments; i += 1) {
      const t = i / (foamSegments - 1);
      const x = 11.2 - t * 3.0;
      const profile = hullProfileAt(x);
      const beam = profile.waterlineHalfBeam;
      for (const zSign of [-1, 1]) {
        foamTransforms.push({
          position: new THREE.Vector3(x, 0.05 + 0.08 * t, zSign * (beam + 0.12 + t * 0.55)),
          rotation: new THREE.Euler(0, zSign * t * 0.5, 0),
          scale: new THREE.Vector3(0.45 + 0.55 * t, 0.35 + 0.8 * t, 1),
        });
      }
    }
    const foamMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: 0xdcece8,
        roughness: 0.9,
        metalness: 0,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
      }),
    );
    this.proceduralGroup.add(this.instanced(foamGeometry, foamMaterial, foamTransforms, false));
  }

  private geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private material<T extends THREE.Material>(material: T): T {
    this.materials.push(material);
    return material;
  }

  private shadow(object: THREE.Mesh): void {
    object.castShadow = true;
    object.receiveShadow = true;
  }

  /** Load the optional deck-prop GLBs once and add them when they land. */
  private async preloadProps(): Promise<void> {
    if (this.propLoadStarted) return;
    this.propLoadStarted = true;
    await this.library.load(['barrel', 'crate']);
    if (this.disposed) return;
    this.buildGlbProps();
  }

  /** Add instanced GLB barrels/crates; each prop set is a single draw call. */
  private buildGlbProps(): void {
    const barrel = this.library.extractGeometry('barrel');
    if (barrel) this.addGlbProp(barrel.geometry, barrel.material, BARREL_TRANSFORMS, BARREL_SCALE);
    const crate = this.library.extractGeometry('crate');
    if (crate) this.addGlbProp(crate.geometry, crate.material, CRATE_TRANSFORMS, CRATE_SCALE);
  }

  /** Build one instanced mesh for a shared single-material prop set. */
  private addGlbProp(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    transforms: InstanceTransform[],
    scale: number,
  ): void {
    const template = new THREE.Mesh(geometry, material);
    template.castShadow = false; // decorative props stay out of the shadow map
    template.receiveShadow = true;
    const placed = transforms.map((transform) => ({
      position: transform.position,
      rotation: transform.rotation,
      scale: new THREE.Vector3(scale, scale, scale),
    }));
    const mesh = instanceStatic(template, placed, material);
    this.glbPropMeshes.push(mesh);
    this.group.add(mesh);
  }

  /**
   * Load the realistic hero hull (Poly Haven dutch_ship_medium) once and, if it
   * lands, overlay it as the ship's exterior shell. The procedural hull stays
   * as the never-throws fallback and is only hidden after the GLB actually
   * loads.
   */
  private async preloadHeroHull(): Promise<void> {
    if (this.heroHullLoadStarted) return;
    this.heroHullLoadStarted = true;
    await this.library.load(['hero-hull']);
    if (this.disposed) return;
    this.buildHeroHull();
  }

  /** Add the loaded hero hull shell and hide the procedural lofted skin. */
  private buildHeroHull(): void {
    const handle = this.library.create('hero-hull');
    if (!handle) return; // never-throws fallback: procedural hull stays visible

    const shell = handle.scene;
    shell.name = 'hero-hull';
    // Hides the baked rigging material (the procedural masts/rigging own it)
    // and re-asserts sRGB/linear colour spaces + anisotropy + env intensity.
    applyPbrDiscipline(shell, { envMapIntensity: 0.55 });
    this.fitHeroHull(shell);

    shell.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if ((mesh as THREE.Mesh).isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    this.group.add(shell);
    this.heroHullGroup = shell;

    // The GLB shell provides the complete realistic ship (hull, deck, masts, sails, cabin),
    // so the procedural fallback ship is hidden.
    this.proceduralGroup.visible = false;
  }

  /**
   * Fit the authored hero hull (metres, bow +X, waterline y=0) onto the
   * procedural planform: uniform scale so the lengths match, waterline stays on
   * y=0 and the hull is centred on the deck line.
   *
   * Seam (documented): the hero hull is ~0.45 m narrower per side than the
   * procedural deck planform, so the procedural deck-slab edges overhang that
   * difference; station anchors, masts, sails and deck props all keep their
   * existing positions on the procedural deck line (y ≈ 1.875–1.9).
   */
  private fitHeroHull(shell: THREE.Group): void {
    const box = new THREE.Box3();
    shell.updateWorldMatrix(true, true);
    shell.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!(mesh as THREE.Mesh).isMesh || mesh.visible === false) return;
      mesh.updateWorldMatrix(true, false);
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      box.union(
        new THREE.Box3().copy(mesh.geometry.boundingBox as THREE.Box3).applyMatrix4(mesh.matrixWorld),
      );
    });
    if (box.isEmpty()) return;

    const heroLength = Math.max(0.001, box.max.x - box.min.x);
    const proceduralLength = HULL_STATIONS[HULL_STATIONS.length - 1].x - HULL_STATIONS[0].x;
    const scale = proceduralLength / heroLength;
    const heroCenterX = (box.min.x + box.max.x) / 2;
    const heroCenterZ = (box.min.z + box.max.z) / 2;
    const targetCenterX = (HULL_STATIONS[0].x + HULL_STATIONS[HULL_STATIONS.length - 1].x) / 2;

    shell.scale.setScalar(scale);

    // If the model bounding box is vertically centered (e.g. ghost pirate ship whose
    // raw bounds span [-63, +63]), offset Y so its main deck line (-41.0 in model space)
    // aligns precisely with the procedural deck height (y ≈ 1.875). Rotate 180° around Y
    // so its bow points forward (+X) and stern castle aft (-X).
    // For models whose origin is already authored at the waterline (box.min.y * scale >= -3.0),
    // keep yOffset at 0 and rotation at 0.
    let yOffset = 0;
    if (box.min.y * scale < -3.0) {
      const modelDeckY = -41.0;
      yOffset = 1.875 - modelDeckY * scale;
      shell.rotation.y = Math.PI;
      shell.position.set(targetCenterX + heroCenterX * scale, yOffset, heroCenterZ * scale);
    } else {
      shell.rotation.y = 0;
      shell.position.set(targetCenterX - heroCenterX * scale, 0, -heroCenterZ * scale);
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    for (const instanced of this.instancedMeshes) instanced.dispose();
    for (const prop of this.glbPropMeshes) {
      this.group.remove(prop);
      prop.dispose();
    }
    this.glbPropMeshes.length = 0;
    this.group.remove(this.proceduralGroup);
    disposeObject3D(this.proceduralGroup);
    if (this.heroHullGroup) {
      this.group.remove(this.heroHullGroup);
      disposeObject3D(this.heroHullGroup);
      this.heroHullGroup = null;
    }
    if (this.ownsLibrary) this.library.dispose();
    for (const cloth of this.clothSails) cloth?.dispose();
    this.sailGroups.length = 0;
    this.sailMeshes.length = 0;
    this.sailBasePositions.length = 0;
    this.sailLevelFractions.length = 0;
    this.clothSails.length = 0;
    this.lanternMaterials.length = 0;
    this.stationMarkerFreeColors.length = 0;
    this.markerMesh = null;
    this.flagMesh = null;
    this.flagGeometry = null;
    this.flagBasePositions = null;
  }
}
